import * as _ from 'lodash'
import {PAdbContext} from "../PA.Core/PAdbContext";
import * as AsyncLock from 'async-lock';
import {WB_PeriodRelationshipRow, WB_PeriodRow} from "../PA.Core/PAdbSchemaRows";
import {esWB_Period, esWB_Period_Relationship} from "../PA.Core/PAdbSchema";
import {PeriodTypeService} from './PeriodTypeService';
import {DateUtils} from "../PA.Core/Basic/Dates";
import {ILease} from "../PA.Utils/PA.UtilsIF";
import {LeaseRegistry} from "../PA.Utils/LeaseRegistry";
import {GenericLease} from '../PA.Utils/GenericLease';


export class PeriodService {

    private static dctPeriodByWBID: { [id: number]: WB_PeriodRow } = {};
    private static dctPeriodByID: { [id: string]: WB_PeriodRow } = {};
    private static dctChildrenByParentWbID: { [id: number]: WB_PeriodRow[] } = {};
    private static dctChildByParentIDLevel: { [id: string]: WB_PeriodRow[] } = {}; //keyed by parent wb id + child type ID
    private static dctChildByParentWbIDChildTypeWbID: { [id: string]: WB_PeriodRow[] } = {}; //keyed by parent period wb ID + child type wb ID
    private static dctPeriodTypeWbIdToDateRange: { [id: string]: WB_PeriodRow[] } = {}; //key is WB_PERIODTYPE_ID + StartDt-EndDt
    private static dctParentByChildWbID: {[id:number]: WB_PeriodRow} = {}; //key is child period WB_ID
    private static dctPeriodByPeriodTypeWbID: {[periodTypeWbID: number]: WB_PeriodRow[]} = {}; //key is WB_PERIODM_ID

    private static dctPeriodByMdWBIdPeriodId: { [mdId: number]: { [periodId: string]: WB_PeriodRow } } = {};

    private static _dbContext: PAdbContext;

    private static dctLock: any = new AsyncLock();
    private static dctChldLock: any = new AsyncLock();
    private static dctRangelock: any = new AsyncLock();

    private static bIsLoaded: boolean = false;
    private static _lease: ILease;

    static getPeriodByWBID(periodWBID: number): Promise<WB_PeriodRow> {

        return new Promise<WB_PeriodRow>((resolve, error) => {

            PeriodService.dctLock.acquire('global',
                (done: any) => {

                    PeriodService.checkLease()
                        .then((ignore) => {
                            if (periodWBID in PeriodService.dctPeriodByWBID) {
                                done(null, PeriodService.dctPeriodByWBID[periodWBID]);
                                resolve(PeriodService.dctPeriodByWBID[periodWBID]);
                                return;
                            } else if (PeriodService.bIsLoaded && !(periodWBID in PeriodService.dctPeriodByWBID)) {
                                done(null, PeriodService.dctPeriodByWBID[periodWBID]);
                                resolve(undefined);
                                return;
                            }

                            PeriodService.loadFromDB()
                                .then((res) => {
                                    done(null, PeriodService.dctPeriodByWBID[periodWBID]);
                                    return PeriodService.dctPeriodByWBID[periodWBID];
                                })
                                .then((targetType) => {
                                    resolve(targetType);
                                })
                                .catch((err2) => {
                                    error(err2);
                                })
                        })
                        .catch((err) => {
                            error(err);
                        })
                }
            )
        })
    }

    static async getPeriodsByWBID(periodWBIDs: number[]): Promise<WB_PeriodRow[]> {
        let retVal: WB_PeriodRow[] = [];
        let period: WB_PeriodRow;

        for (let aWbID of periodWBIDs) {
            period = await PeriodService.getPeriodByWBID(aWbID);
            if (!_.isUndefined(period)) {
                retVal.push(period);
            }
        }

        return retVal;
    }

    static getPeriodByPeriodID(periodID: string): Promise<WB_PeriodRow> {

        return new Promise<WB_PeriodRow>((resolve, error) => {

            PeriodService.dctLock.acquire('global',
                (done: any) => {

                    PeriodService.checkLease()
                        .then((ignore) => {
                            if (periodID in PeriodService.dctPeriodByID) {
                                done(null, PeriodService.dctPeriodByID[periodID]);
                                resolve(PeriodService.dctPeriodByID[periodID]);
                                return;
                            } else if (PeriodService.bIsLoaded && !(periodID in PeriodService.dctPeriodByID)) {
                                done(null, PeriodService.dctPeriodByID[periodID]);
                                resolve(undefined);
                                return;
                            }

                            PeriodService.loadFromDB()
                                .then((res) => {
                                    done(null, PeriodService.dctPeriodByID[periodID]);
                                    return PeriodService.dctPeriodByID[periodID];
                                })
                                .then((targetType) => {
                                    resolve(targetType);
                                })
                                .catch((err2) => {
                                    error(err2);
                                })
                        })
                        .catch((err) => {
                            error(err);
                        })
                }
            )

        })
    }

    static async getPeriodsByPeriodID(periodIDs: string[]) : Promise<WB_PeriodRow[]> {
        let retVal: WB_PeriodRow[] = [];
        for (let aPeriodID of periodIDs) {
            let periodRow = await PeriodService.getPeriodByPeriodID(aPeriodID);
            if (periodRow) {
                retVal.push(periodRow);
            }
        }
        return retVal;
    }

    static getPeriodByMdIdPeriodId(wbMdId: number, periodId: string): Promise<WB_PeriodRow> {

        return new Promise<WB_PeriodRow>((resolve, error) => {
            PeriodService.dctLock.acquire('global',
                (done: any) => {
                    PeriodService.checkLease()
                        .then(() => {
                            if (!(wbMdId in PeriodService.dctPeriodByMdWBIdPeriodId)) {
                                PeriodService.dctPeriodByMdWBIdPeriodId[wbMdId] = {};
                            }
                            const dctByMdId: { [id: string]: WB_PeriodRow } = PeriodService.dctPeriodByMdWBIdPeriodId[wbMdId];
                            if (periodId in dctByMdId) {
                                done(null, dctByMdId[periodId]);
                                resolve(dctByMdId[periodId]);
                            } else if (PeriodService.bIsLoaded && !(periodId in dctByMdId)) {
                                done(null, dctByMdId[periodId]);
                                resolve(undefined);
                                return;
                            }

                            PeriodService.loadFromDB()
                                .then(() => {
                                    done(null, dctByMdId[periodId]);
                                    resolve(dctByMdId[periodId]);
                                })
                                .catch((err) => {
                                    error(err);
                                })
                        })
                        .catch((err) => {
                            error(err);
                        })
                }
            )
        })
    }

    static async getPeriodsByMdIdPeriodId(wbMdId: number, periodIds: string[]): Promise<WB_PeriodRow[]> {
        const retVal: WB_PeriodRow[] = [];
        for (const periodId of periodIds) {
            const row: WB_PeriodRow = await PeriodService.getPeriodByMdIdPeriodId(wbMdId, periodId);
            if (row) {
                retVal.push(row);
            }
        }
        return retVal;
    }

    static getParentByChildWbID(wbPeriodID: number) : Promise<WB_PeriodRow> {
        return new Promise<WB_PeriodRow>((resolve, error) => {
            PeriodService.dctLock.acquire('global',
                (done: any) => {
                    PeriodService.checkLease()
                        .then((ignore) => {
                            if (wbPeriodID in PeriodService.dctParentByChildWbID) {
                                done(null, PeriodService.dctParentByChildWbID[wbPeriodID]);
                                resolve(PeriodService.dctParentByChildWbID[wbPeriodID]);
                                return;
                            } else if (PeriodService.bIsLoaded && !(wbPeriodID in PeriodService.dctParentByChildWbID)) {
                                done(null, PeriodService.dctParentByChildWbID[wbPeriodID]);
                                resolve(undefined);
                                return;
                            }

                            PeriodService.loadFromDB()
                                .then((ignore) => {
                                    done(null, PeriodService.dctParentByChildWbID[wbPeriodID]);
                                    resolve(PeriodService.dctParentByChildWbID[wbPeriodID]);
                                    return;
                                })
                                .catch((err) => {
                                    error(err);
                                })
                        })
                        .catch((err) => {
                            error(err);
                        })
                })
        })
    }

    static getAllChildrenByWBIDChildTypeWbId(childPeriodTypeWbID: number, prntPeriodWbID: number): Promise<WB_PeriodRow[]> {

        return new Promise<WB_PeriodRow[]>((resolve, error) => {
            let dctKey: string = prntPeriodWbID + "-" + childPeriodTypeWbID;

            PeriodService.dctChldLock.acquire('global',
                (done: any) => {

                    PeriodService.checkLease()
                        .then((ignore) => {
                            if (dctKey in PeriodService.dctChildByParentWbIDChildTypeWbID) {
                                done(null, PeriodService.dctChildByParentWbIDChildTypeWbID[dctKey]);
                                resolve(PeriodService.dctChildByParentWbIDChildTypeWbID[dctKey]);
                                return;
                            }

                            PeriodService.getPeriodByWBID(prntPeriodWbID)
                                .then((parentPeriod) => {
                                    if (_.isUndefined(parentPeriod)) {
                                        resolve([]);
                                        return;
                                    }

                                    let children = {};
                                    PeriodService.findChildrenAtLevel(parentPeriod, childPeriodTypeWbID, children);
                                    PeriodService.dctChildByParentWbIDChildTypeWbID[dctKey] = _.orderBy(_.values(children), ['PERIOD_START_DT'], ['asc']);
                                    done(null, PeriodService.dctChildByParentWbIDChildTypeWbID[dctKey]);
                                    resolve(PeriodService.dctChildByParentWbIDChildTypeWbID[dctKey]);
                                    return;
                                })
                                .catch((err) => {
                                    error(err);
                                })
                        })
                        .catch((err) => {
                            error(err);
                        })
                })
        })
    }

    static getAllChildrenByWBIDChildTypeWbIdWithinRange(childPeriodTypeWbID: number, prntPeriodWbID: number, effectiveStartDt: Date, effectiveEndDt: Date): Promise<WB_PeriodRow[]> {
        return new Promise<WB_PeriodRow[]>((resolve, error) => {
                PeriodService.getAllChildrenByWBIDChildTypeWbId(childPeriodTypeWbID, prntPeriodWbID)
                    .then((periods) => {
                        let retVal: WB_PeriodRow[] = [];
                        _.forEach(periods, (aPeriod: WB_PeriodRow) => {
                            if (DateUtils.RangesOverlap({StartDt: aPeriod.PeriodStartDt, EndDt: aPeriod.PeriodEndDt}, {StartDt: effectiveStartDt, EndDt: effectiveEndDt})) {
                                retVal.push(aPeriod);
                            }
                        });
                        resolve(retVal);
                        return;
                    })
                    .catch((err) => {
                        error(err);
                    })
        })
    }

    static getPeriodsByPeriodTypeWbIDDateRange(periodTypeWbID: number, effectiveStartDt: Date, effectiveEndDt: Date): Promise<WB_PeriodRow[]> {

        return new Promise<WB_PeriodRow[]>((resolve, error) => {

            PeriodService.checkLease()
                .then((ignore) => {
                    PeriodService.dctRangelock.acquire('global',
                        (done: any) => {
                            if (PeriodService.bIsLoaded) {
                                let retVal = PeriodService.findPeriodsByTypeDateRange(periodTypeWbID, effectiveStartDt, effectiveEndDt);
                                done(null);
                                resolve(retVal);
                                return;
                            } else {
                                PeriodService.loadFromDB()
                                    .then((ignore) => {
                                        let retVal = PeriodService.findPeriodsByTypeDateRange(periodTypeWbID, effectiveStartDt, effectiveEndDt);
                                        done(null);
                                        resolve(retVal);
                                        return;
                                    })
                                    .catch((err) => {
                                        done(null);
                                        error(err);
                                    })
                            }
                        })

                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    static getPeriodsByPeriodTypeIDDateRange(periodTypeID: string, effectiveStartDt: Date, effectiveEndDt: Date): Promise<WB_PeriodRow[]> {

        return new Promise<WB_PeriodRow[]>((resolve, error) => {

            PeriodTypeService.getPeriodTypeByTypeID(periodTypeID)
                .then((periodTypeWbID) => {
                    if (!periodTypeWbID) {
                        resolve([]);
                        return;
                    }
                    PeriodService.getPeriodsByPeriodTypeWbIDDateRange(periodTypeWbID.cif.WB_PERIODM_ID, effectiveStartDt, effectiveEndDt)
                        .then((periodRows) => {
                            resolve(periodRows);
                        })
                })
        })
    }

    static getPeriodsByPeriodTYpeWbID(periodTypeWbID: number) : Promise<WB_PeriodRow[]> {
        return new Promise<WB_PeriodRow[]>((resolve, error) => {
            PeriodService.dctLock.acquire('global',
                (done: any) => {
                    PeriodService.checkLease()
                                 .then((ignore) => {
                                     if (periodTypeWbID in PeriodService.dctPeriodByPeriodTypeWbID) {
                                         done(null, PeriodService.dctPeriodByPeriodTypeWbID);
                                         resolve (PeriodService.dctPeriodByPeriodTypeWbID[periodTypeWbID]);
                                         return;
                                     } else if (PeriodService.bIsLoaded && !(periodTypeWbID in PeriodService.dctPeriodByPeriodTypeWbID)) {
                                         done(null, PeriodService.dctPeriodByPeriodTypeWbID);
                                         resolve(undefined);
                                         return;
                                     }

                                     PeriodService.loadFromDB()
                                                  .then((ignore) => {
                                                      done(null, PeriodService.dctPeriodByPeriodTypeWbID);
                                                      resolve (PeriodService.dctPeriodByPeriodTypeWbID[periodTypeWbID]);
                                                      return;
                                                  })
                                                  .catch((err) => {
                                                      error(err);
                                                  })
                                 })
                                 .catch((err) => {
                                     error(err);
                                 })
                })
        })
    }

    private static loadFromDB(): Promise<void> {

        return new Promise<void>((resolve, error) => {

            PeriodService.bIsLoaded = false;

            if (_.isUndefined(PeriodService._dbContext)) {
                PeriodService.instantiateDbContext();
            }

            PeriodService._dbContext.findAll(esWB_Period, {})
                .then((results) => {
                    if (_.isArray(results)) {
                        let periods: WB_PeriodRow[] = results as WB_PeriodRow[];

                        _.forEach(periods, (aPeriod) => {
                            PeriodService.dctPeriodByWBID[aPeriod.cif.WB_PERIOD_ID] = aPeriod;
                            PeriodService.dctPeriodByID[aPeriod.cif.PERIOD_ID] = aPeriod;

                            if (!(aPeriod.cif.WB_MDSET_ID in PeriodService.dctPeriodByMdWBIdPeriodId)) {
                                PeriodService.dctPeriodByMdWBIdPeriodId[aPeriod.cif.WB_MDSET_ID] = {};
                            }
                            PeriodService.dctPeriodByMdWBIdPeriodId[aPeriod.cif.WB_MDSET_ID][aPeriod.cif.PERIOD_ID] = aPeriod;

                            if (!(aPeriod.cif.WB_PERIODM_ID in PeriodService.dctPeriodByPeriodTypeWbID)) {
                                PeriodService.dctPeriodByPeriodTypeWbID[aPeriod.cif.WB_PERIODM_ID] = [];
                            }

                            PeriodService.dctPeriodByPeriodTypeWbID[aPeriod.cif.WB_PERIODM_ID].push(aPeriod);
                        });

                        PeriodService._dbContext.findAll(esWB_Period_Relationship, {})
                            .then((rels) => {
                                if (_.isArray(rels)) {
                                    let periodRels: WB_PeriodRelationshipRow[] = rels as WB_PeriodRelationshipRow[];

                                    _.forEach(periodRels, (aRel) => {
                                        if (!_.isUndefined(PeriodService.dctPeriodByWBID[aRel.cif.WB_PERIOD_ID])) {
                                            if (_.isUndefined(PeriodService.dctChildrenByParentWbID[aRel.cif.WB_PERIOD_ID_PRNT])) {
                                                PeriodService.dctChildrenByParentWbID[aRel.cif.WB_PERIOD_ID_PRNT] = [];
                                            }
                                            PeriodService.dctChildrenByParentWbID[aRel.cif.WB_PERIOD_ID_PRNT].push(PeriodService.dctPeriodByWBID[aRel.cif.WB_PERIOD_ID]);
                                        }
                                        if (!(_.isUndefined(PeriodService.dctPeriodByWBID[aRel.cif.WB_PERIOD_ID_PRNT]))) {
                                            PeriodService.dctParentByChildWbID[aRel.cif.WB_PERIOD_ID] = PeriodService.dctPeriodByWBID[aRel.cif.WB_PERIOD_ID_PRNT];
                                        }
                                    });

                                    PeriodService.bIsLoaded = true;
                                    resolve();
                                }
                            })
                            .catch((err) => {
                                error(err);
                            });
                    } else {
                        error("Unrecognized Period Search Results")
                    }
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    private static findChildrenAtLevel(parentPeriod: WB_PeriodRow, levelID: number, dctResults: { [id: number]: WB_PeriodRow }) {

        if (parentPeriod.cif.WB_PERIODM_ID == levelID) {
            dctResults[parentPeriod.cif.WB_PERIOD_ID] = parentPeriod;
            return;
        }

        //the cache will already be loaded, assuming the loadFromDB function has been called already
        let children = PeriodService.dctChildrenByParentWbID[parentPeriod.cif.WB_PERIOD_ID];

        if (!_.isEmpty(children)) {
            _.forEach(children, (aChild) => {
                PeriodService.findChildrenAtLevel(aChild, levelID, dctResults);
            })
        }

    }

    private static findPeriodsByTypeDateRange(periodTypeWbID: number, effectiveStartDt: Date, effectiveEndDt: Date): WB_PeriodRow[] {

        let dctKey: string = [periodTypeWbID, DateUtils.convertToSAPBW(effectiveStartDt), DateUtils.convertToSAPBW(effectiveEndDt)].join('-');

        if (dctKey in PeriodService.dctPeriodTypeWbIdToDateRange) {
            return PeriodService.dctPeriodTypeWbIdToDateRange[dctKey];
        } else if (PeriodService.bIsLoaded && !(dctKey in PeriodService.dctPeriodTypeWbIdToDateRange)) {
            let retVal: WB_PeriodRow[] = _.orderBy(_.filter(_.values(PeriodService.dctPeriodByWBID), (aPeriod: WB_PeriodRow) => {
                return aPeriod.cif.WB_PERIODM_ID == periodTypeWbID && DateUtils.RangeDatesOverlap(DateUtils.convertFromSAPBW(aPeriod.cif.PERIOD_START_DT), DateUtils.convertFromSAPBW(aPeriod.cif.PERIOD_END_DT), effectiveStartDt, effectiveEndDt);
            }), (aVal) => {
                return aVal.PeriodStartDt
            }, ["asc"]);

            PeriodService.dctPeriodTypeWbIdToDateRange[dctKey] = retVal;
            return retVal;
        }

        return [];
    }

    private static createLease() {
        PeriodService._lease = new GenericLease(LeaseRegistry.Lease_Type_Period);
    }

    private static async checkLease(): Promise<void> {
        if (_.isUndefined(PeriodService._lease)) {
            PeriodService.createLease();
        }

        if (await PeriodService._lease.hasDataChanged()) {
            await PeriodService.reset();
            PeriodService._lease.resetLease();
        }
    }

    private static async reset(): Promise<void> {

        PeriodService.instantiateDbContext();

        let arr: WB_PeriodRow[] = _.values(PeriodService.dctPeriodByWBID);
        await PeriodService._dbContext.purgeCache(esWB_Period, arr);

        PeriodService.dctChildrenByParentWbID = {};
        PeriodService.dctChildByParentIDLevel = {};
        PeriodService.dctPeriodByWBID = {};
        PeriodService.dctPeriodByID = {};

        PeriodService.bIsLoaded = false;
    }

    private static instantiateDbContext() {
        PeriodService._dbContext = new PAdbContext();
    }
}